/**
 * useNotificationContentLocalization
 *
 * The notification service (`tms-api-notifications`) only ever seeds ENGLISH
 * `notification_templates` rows and renders `subject`/`rendered_body` server-side
 * before the FE ever sees them — so a German/French/Turkish user viewing a
 * PRODUCT_RECALL alert always sees raw English text.
 *
 * Rather than change the backend, this maps the KNOWN English template shape
 * back to its placeholder values (same technique used elsewhere in this app for
 * other server-seeded enum/value data, e.g. `useEnumTranslation.ts`) and
 * re-renders it with real per-locale translations. Falls back to the raw
 * server text for any event type/format we don't recognise.
 */
import { useLocale } from './useLocale';

// Mirrors the fixed English templates seeded in
// apps/tms-api-onboarding/src/v1/repo/tenant-seed.repo.ts for PRODUCT_RECALL/IN_APP.
const PRODUCT_RECALL_SUBJECT_RE = /^Product Recall (.*)$/s;
const PRODUCT_RECALL_BODY_RE = /^Product SKU (.*) \(Batch (.*)\) has been recalled\. Reason (.*)\. Please take immediate action\.$/s;

export function useNotificationContentLocalization() {
  const { t } = useLocale();

  function localizeSubject(eventType: string | undefined, rawSubject: string): string {
    if (eventType !== 'PRODUCT_RECALL') return rawSubject;
    const match = PRODUCT_RECALL_SUBJECT_RE.exec(rawSubject);
    if (!match) return rawSubject;
    return t('notifications.critical.productRecallSubject', { title: match[1] });
  }

  function localizeBody(eventType: string | undefined, rawBody: string): string {
    if (eventType !== 'PRODUCT_RECALL') return rawBody;
    const match = PRODUCT_RECALL_BODY_RE.exec(rawBody);
    if (!match) return rawBody;
    const [, skus, batches, reason] = match;
    return t('notifications.critical.productRecallBody', { skus, batches, reason });
  }

  return { localizeSubject, localizeBody };
}
